import { Component, Input, OnChanges, SimpleChanges, signal, ViewChild } from '@angular/core';
import { FormArray, FormGroup, UntypedFormControl } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { validateCustomProfile } from 'src/app/perivis/shared/validators/custom-profile.validator';
import { CustomIPP, InternalPressureProfileType, PressurePoint } from '../../models/internal-pressure-profile.model';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';
import { GetValueFromFeet } from 'src/app/perivis/shared/helpers/units.helper';
import { UnitsGridInputComponent } from 'src/app/shared/components/units-grid-input/units-grid-input.component';
import { UnitsGridColumn, UnitsGridCommitEvent, UnitsGridPasteRange } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import {
  createCustomIppColumns,
  createCustomIppRow,
  ensureCustomIppPlugAnchors,
  ensureTrailingInputRow,
  extractCustomIppRows,
  findRowIndexNearDepth,
  getCustomIppCellTooltip,
  getCustomIppRowActionIconSrc,
  getCustomIppRowActionLabel,
  isCustomIppProtectedRow,
  populateCustomIppFormArray
} from './custom-ipp.helper';

export interface CustomIppForm {
  type: InternalPressureProfileType.CUSTOMIPP;
  pressurePoints: PressurePoint[];
  plugMd: UomQuantityInput;
  hasPlug: boolean;
}

@Component({
    selector: 'app-custom-ipp',
    template: `
      <app-tool-loader [isLoading]="isLoading()" [positionUnset]="true">
        <form [formGroup]="form.formGroup">

          <div class="space-x-2 flex items-center">
            <div class="flex space-x-2 pb-4">
              <p-checkbox
                size="small"
                class="mt-5"
                binary="true"
                pTooltip="Has Plug"
                [formControlName]="form.formControlNames.hasPlug"
                (change)="hasPlugChange()"
              />

              <app-uom-input
                [class.pointer-events-none]="!form.formGroup.controls.hasPlug.value"
                [class.opacity-50]="!form.formGroup.controls.hasPlug.value"
                [formControl]="form.formGroup.controls.plugMd"
                label="Plug Depth"
                [unitLabel]="userUnits.longLengths"
                [control]="'plugMd'"
                [default]="currentString.shoeMd"
                [min]="0"
                [max]="currentString.shoeMd"
                [required]="true"
                (focusout)="onPlugDepthChange()"
              ></app-uom-input>
            </div>
            @if (customIppRows.length > 10 && form.formGroup.controls.hasPlug.value) {
              <p-button class="pt-1" pTooltip="Scroll to plug" outlined="true" size="small" icon="pi pi-sync" (onClick)="scrollToPlugDepth()"></p-button>
            }
          </div>

          @if (form.formGroup.controls.pressurePoints.errors?.lastPointAboveShoe) {
            <p
              class="validation-error"
              >Must be specified from hanger: {{ currentString?.hangerMd.toFixed(3) }} {{ userUnits.longLengths }} and to or
              below shoe: {{ currentString?.shoeMd.toFixed(3) }} {{ userUnits.longLengths }}, but above well TD: {{ wellTd.toFixed(3) }} {{ userUnits.longLengths }}
            </p>
          }
          <app-units-grid-input
            #customIppTable
            [tableTitle]="'Custom Internal Pressures'"
            [rows]="customIppRows"
            [columns]="columns"
            [userUnits]="userUnits"
            [enableScroll]="true"
            [scrollHeight]="'300px'"
            [allowDeleteRows]="true"
            [minRows]="1"
            [createRow]="createRow"
            [rowCanBeDeleted]="canDeleteCustomIppRow"
            [rowActionLabel]="getIppRowActionLabel"
            [rowActionIconSrc]="getIppRowActionIconSrc"
            [cellTooltip]="getIppCellTooltip"
            [commitMode]="'debounced'"
            [commitDebounceMs]="300"
            (cellEdited)="onCellEdited()"
            (rowDeleted)="onRowDeleted($event)"
            (rowsReset)="onRowsReset()"
            (pasteApplying)="onPasteApplying($event)"
            (pasteApplied)="onPasteApplied()"
            (commitRequested)="onGridCommit($event)"
          />
        </form>
      </app-tool-loader>
    `,
    providers: subformComponentProviders(CustomIppComponent),
    standalone: false
})
export class CustomIppComponent implements OnChanges {
  constructor() {
    // ngx-sub-form patches `writeValue` (the CVA hook) onto `this` when `form` is created below.
    // For CVA sub-forms, initialization MUST happen only via writeValue to avoid racing against it.
    // Defer to next microtask to let ngx-sub-form's async form updates complete before reading values.
    const cva = this as unknown as { writeValue(value: CustomIPP | null): void };
    const applyWrittenValue = cva.writeValue.bind(cva);
    cva.writeValue = (value: CustomIPP | null): void => {
      applyWrittenValue(value);
      Promise.resolve().then(() => this.setDataAndDefaults());
    };
  }

  @Input() currentString: any;
  @Input() userUnits: UserUnitsModel;
  @Input() wellTd: number;
  @Input() defaultPressures: PressurePoint[];

  @ViewChild("customIppTable")
  public customIppTable: UnitsGridInputComponent;

  public readonly columns = createCustomIppColumns();
  public readonly createRow = createCustomIppRow;
  public readonly getIppRowActionLabel = (_row: FormGroup, rowIndex: number): string | null =>
    getCustomIppRowActionLabel(
      this.customIppRows.controls,
      rowIndex,
      this.form.formGroup.controls.hasPlug.value,
      this.form.formGroup.controls.plugMd.value?.control
    );
  public readonly getIppRowActionIconSrc = (_row: FormGroup, rowIndex: number): string | null =>
    getCustomIppRowActionIconSrc(
      this.customIppRows.controls,
      rowIndex,
      this.form.formGroup.controls.hasPlug.value,
      this.form.formGroup.controls.plugMd.value?.control
    );
  public readonly getIppCellTooltip = (_row: FormGroup, column: UnitsGridColumn, rowIndex: number): string | null =>
    getCustomIppCellTooltip(
      this.customIppRows.controls,
      rowIndex,
      column.field,
      this.form.formGroup.controls.hasPlug.value,
      this.form.formGroup.controls.plugMd.value?.control
    );
  public readonly canDeleteCustomIppRow = (row: FormGroup, rowIndex: number): boolean =>
    !isCustomIppProtectedRow(
      this.customIppRows.controls,
      rowIndex,
      this.form.formGroup.controls.hasPlug.value,
      this.form.formGroup.controls.plugMd.value?.control
    );
  public customIppRows = new FormArray<FormGroup>([]);

  public plugDepthDisabled: boolean;
  public isLoading = signal<boolean>(true);

  private _syntheticPlugDepths: number[] = [];
  private _isRebuildingPlugAfterPaste = false;

  public form = createForm<CustomIPP, CustomIppForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(InternalPressureProfileType.CUSTOMIPP),
      pressurePoints: new UntypedFormControl(null),
      hasPlug: new UntypedFormControl(false),
      plugMd: new UntypedFormControl(null)
    },
    toFormGroup: (obj: CustomIPP | null): CustomIppForm | null => {
      if (!obj) {
        return null;
      }

      this.plugDepthDisabled = !obj.plugMd;

      if (!obj.plugMd || obj.plugMd === 0) {
        obj.plugMd = this.currentString.shoeMd;
      }

      return {
        pressurePoints: obj.pressurePoints,
        plugMd: { control: obj.plugMd },
        hasPlug: obj.hasPlug,
        type: obj.type
      };
    },
    fromFormGroup: (formValue: CustomIppForm): CustomIPP | null => {
      const { pressurePoints, plugMd, hasPlug, ...commonValues } = formValue;
      return {
        pressurePoints: pressurePoints,
        plugMd: plugMd?.control ? +plugMd?.control : this.currentString.shoeMd,
        hasPlug: hasPlug,
        ...commonValues
      };
    }
  });

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.defaultPressures || !this.defaultPressures?.length) {
      return;
    }

    Promise.resolve().then(() => {
      if (!(this.form.formGroup.controls.pressurePoints?.value?.length > 0)) {
        this.setDataAndDefaults();
      }
    });
  }

  private setDataAndDefaults(): void {
    this.form.formGroup.controls.pressurePoints.addValidators(validateCustomProfile(this.currentString, this.wellTd));

    const existingPoints = this.form.formGroup.controls.pressurePoints?.value;
    const points = existingPoints?.length > 0 ? existingPoints : this.createInitialCustomPressurePoints();

    if (!(existingPoints?.length > 0) && (this.defaultPressures?.length ?? 0) === 0) {
      this.customIppRows.clear();
      this.customIppRows.push(createCustomIppRow({ measuredDepth: 0 }));
      this.disablePlugDepths();
      this.isLoading.set(false);
      return;
    }

    if (!(existingPoints?.length > 0)) {
      this.form.formGroup.controls.pressurePoints.setValue(points);
    }

    populateCustomIppFormArray(this.customIppRows, points);

    if (this.form.formGroup.controls.hasPlug.value) {
      this.onPlugDepthChange();
    } else {
      this.disablePlugDepths();
    }

    this.isLoading.set(false);
  }

  // Runs synchronously on blur so a new trailing row exists before Tab navigation looks for the next cell.
  public onCellEdited(): void {
    ensureTrailingInputRow(this.customIppRows);
  }

  public onRowDeleted(index: number): void {
    const row = this.customIppRows.at(index);
    if (row && this.canDeleteCustomIppRow(row, index) && index >= 0 && index < this.customIppRows.length) {
      this.customIppRows.removeAt(index);
    }
    ensureTrailingInputRow(this.customIppRows);
    this.syncPressurePoints();
  }

  public onRowsReset(): void {
    const points = this.createInitialCustomPressurePoints();
    populateCustomIppFormArray(this.customIppRows, points);
    this.form.formGroup.controls.pressurePoints.setValue(points);
    this.form.formGroup.controls.hasPlug.setValue(false);
    this.hasPlugChange();
  }

  // Full-width paste must overwrite locked depths before the plug anchors are rebuilt.
  public onPasteApplying(range: UnitsGridPasteRange): void {
    this._isRebuildingPlugAfterPaste = range.startColumnIndex === 0
      && range.columnCount >= 2
      && this.form.formGroup.controls.hasPlug.value;

    if (this._isRebuildingPlugAfterPaste) {
      this.customIppRows.controls.forEach(row => row.get('measuredDepth')?.enable({ emitEvent: false }));
    }
  }

  public onPasteApplied(): void {
    if (!this._isRebuildingPlugAfterPaste) {
      return;
    }

    this._isRebuildingPlugAfterPaste = false;
    // These depths now contain pasted data, not the previously synthesized anchors.
    this._syntheticPlugDepths = [];
    this.onPlugDepthChange();
  }

  public onGridCommit(_event: UnitsGridCommitEvent): void {
    ensureTrailingInputRow(this.customIppRows);
    this.syncPressurePoints();
    if (this.form.formGroup.controls.hasPlug.value) {
      this.disablePlugDepths();
    }
  }

  private syncPressurePoints(): void {
    this.form.formGroup.controls.pressurePoints.setValue(extractCustomIppRows(this.customIppRows));
  }

  private createInitialCustomPressurePoints(): PressurePoint[] {
    const tolerance = 0.001;
    const pressures = (this.defaultPressures ?? [])
      .map(p => ({
        measuredDepth: +p.measuredDepth,
        pressure: +p.pressure.toFixed(3)
      }))
      .filter((p, index) => index === 0 || p.measuredDepth !== 0);

    if (pressures.length > 0) {
      const first = pressures[0];
      const last = pressures[pressures.length - 1];

      const firstVal = Math.abs(first.measuredDepth - (this.currentString?.hangerMd ?? first.measuredDepth));
      if (firstVal >= tolerance && firstVal < 1) {
        first.measuredDepth = (this.currentString?.hangerMd ?? first.measuredDepth) - 0.000001;
      }

      const lastVal = Math.abs(last.measuredDepth - (this.currentString?.shoeMd ?? last.measuredDepth));
      if (lastVal >= tolerance && lastVal < 1) {
        last.measuredDepth = (this.currentString?.shoeMd ?? last.measuredDepth) + 0.000001;
      }
    }

    this._syntheticPlugDepths = [];
    return pressures;
  }

  public hasPlugChange(): void {
    this.plugDepthDisabled = !this.form.formGroup.controls.hasPlug.value;

    if (this.form.formGroup.controls.hasPlug.value) {
      this.onPlugDepthChange();
    } else {
      this._syntheticPlugDepths = [];
      this.disablePlugDepths();
    }
  }

  public onPlugDepthChange(): void {
    let plugDepth = this.form.formGroup.controls.plugMd.value?.control;

    if (plugDepth === null || plugDepth === undefined || plugDepth > this.currentString.shoeMd) {
      plugDepth = this.currentString.shoeMd;
      this.form.formGroup.controls.plugMd.setValue({ control: plugDepth });
    }

    const depthAbovePlug = GetValueFromFeet(0.05, this.userUnits.longLengths);

    const { points, syntheticDepths } = ensureCustomIppPlugAnchors(
      extractCustomIppRows(this.customIppRows),
      plugDepth,
      depthAbovePlug,
      this._syntheticPlugDepths
    );

    this._syntheticPlugDepths = syntheticDepths;

    populateCustomIppFormArray(this.customIppRows, points);
    this.form.formGroup.controls.pressurePoints.setValue(points);
    this.disablePlugDepths();

    if (points.length > 10) {
      this.scrollToPlugDepth();
    }
  }

  public scrollToPlugDepth(): void {
    const plugDepth = this.form.formGroup.controls.plugMd.value?.control;
    if (plugDepth === null || plugDepth === undefined) {
      return;
    }

    const rowIndex = findRowIndexNearDepth(this.customIppRows, plugDepth);
    this.customIppTable?.scrollToRowIndex(rowIndex);
  }

  private disablePlugDepths(): void {
    this.customIppRows.controls.forEach(row => row.get('measuredDepth')?.enable({ emitEvent: false }));

    if (!this.form.formGroup.controls.hasPlug.value) {
      return;
    }

    const plugDepth = this.form.formGroup.controls.plugMd.value?.control;
    if (plugDepth === null || plugDepth === undefined) {
      return;
    }

    const plugRowIndex = findRowIndexNearDepth(this.customIppRows, plugDepth);
    if (plugRowIndex === -1) {
      return;
    }

    this.customIppRows.at(plugRowIndex)?.get('measuredDepth')?.disable({ emitEvent: false });
    this.customIppRows.at(plugRowIndex - 1)?.get('measuredDepth')?.disable({ emitEvent: false });
  }
}
